/**
 * SizilPro — SetupPreview
 * Step 6: Final setup review and lightweight sample preview.
 *
 * This screen no longer uses @react-pdf/renderer.
 * The active PDF path is server-side only.
 *
 * Updated to feel more like a true setup publish screen:
 * - reviews the brand foundation
 * - lets the user choose their builder experience
 * - previews fixed-page direction
 * - finishes setup cleanly without changing the active PDF architecture
 *
 * File: src/modules/proposal-builder/components/setup/SetupPreview.tsx
 */

import { useMemo, useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Loader2,
  CheckCircle2,
  ArrowRight,
  X,
  FileText,
  Building2,
  Palette,
  FileStack,
  Wrench,
  LayoutTemplate,
  Type,
} from "lucide-react";
import {
  useBrandSettings,
  useUpsertBrandSettings,
  useBrandAssets,
  useBrandFonts,
} from "../../hooks/useBrandSettings";
import { useServiceMenu } from "../../hooks/useServiceMenu";
import { useStaticPages } from "../../hooks/useStaticPages";
import type {
  BrandFont,
  BrandPreviewPreferences,
  BrandTypographyPreferences,
  BuiltInFontPreference,
} from "../../types";

interface Props {
  onComplete: () => void;
  isCompleting?: boolean;
}

type BuilderExperience = "prebuilt" | "drag_drop" | "both";

const SAMPLE_PROPOSAL = {
  prospect_name: "Ocean Prime Restaurant",
  prospect_address: "123 Prospect Street",
  prospect_city: "Nashville",
  prospect_state: "TN",
  prospect_zip: "37203",
  project_summary:
    "Daily restaurant cleaning including front of house, back of house, and restrooms.",
  ai_project_summary:
    "Daily cleaning of front of house high-touch areas, restrooms, and back of house floors. Periodic floor care and window service included.",
};


const BUILT_IN_FONT_LABELS: Record<BuiltInFontPreference, string> = {
  inter: "Inter",
  roboto: "Roboto",
  lato: "Lato",
  "open-sans": "Open Sans",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function coerceBuiltInFont(value: unknown): BuiltInFontPreference | null {
  return value === "inter" ||
    value === "roboto" ||
    value === "lato" ||
    value === "open-sans"
    ? value
    : null;
}

function getTypographyPreferences(
  previewPreferences: BrandPreviewPreferences | Record<string, unknown> | null | undefined
): BrandTypographyPreferences | null {
  if (!previewPreferences) return null;
  const typography = previewPreferences.typography;
  return isRecord(typography) ? (typography as BrandTypographyPreferences) : null;
}

function resolveTypographyLabel(
  selection: BrandTypographyPreferences["heading"] | BrandTypographyPreferences["body"] | undefined,
  uploadedFonts: BrandFont[],
  fallbackBuiltIn: BuiltInFontPreference
): string {
  if (selection?.source === "uploaded" && selection.uploadedFontId) {
    const uploaded = uploadedFonts.find((font) => font.id === selection.uploadedFontId);
    if (uploaded?.family_name) return uploaded.family_name;
    if (selection.uploadedFontName) return selection.uploadedFontName;
    return "Uploaded font";
  }

  const builtIn = coerceBuiltInFont(selection?.builtInFont) ?? fallbackBuiltIn;
  return BUILT_IN_FONT_LABELS[builtIn];
}

export function SetupPreview({
  onComplete,
  isCompleting = false,
}: Props) {
  const { data: brand } = useBrandSettings();
  const upsert = useUpsertBrandSettings();
  const { data: services = [] } = useServiceMenu();
  const { data: pages = [] } = useStaticPages();
  const { data: brandAssets = [] } = useBrandAssets();
  const { data: brandFonts = [] } = useBrandFonts();

  const [expanded, setExpanded] = useState(false);
  const [builderExperience, setBuilderExperience] = useState<BuilderExperience>(
    "prebuilt"
  );

  useEffect(() => {
    const next = brand?.default_builder_experience;
    if (next === "prebuilt" || next === "drag_drop" || next === "both") {
      setBuilderExperience(next);
    }
  }, [brand?.default_builder_experience]);

  const activePages = useMemo(
    () => pages.filter((p) => p.active),
    [pages]
  );

  const uploadedFonts = useMemo(
    () => brandFonts.filter((font) => font.source_type === "uploaded"),
    [brandFonts]
  );
  const primaryLogo = brandAssets.find((a) => a.asset_role === "primary_logo");
  const secondaryLogo = brandAssets.find((a) => a.asset_role === "secondary_logo");

  const primaryLogoUrl = brand?.logo_url ?? primaryLogo?.public_url ?? null;
  const secondaryLogoUrl = secondaryLogo?.public_url ?? null;

  const extraBranding = useMemo(
    () =>
      brandAssets.filter(
        (asset) =>
          asset.asset_role !== "primary_logo" && asset.asset_role !== "secondary_logo"
      ),
    [brandAssets]
  );

  const fallbackBuiltInFont =
    coerceBuiltInFont(brand?.font_preference) ?? "inter";

  const typography = useMemo(
    () => getTypographyPreferences(brand?.preview_preferences),
    [brand?.preview_preferences]
  );

  const headingFontLabel = useMemo(
    () =>
      resolveTypographyLabel(
        typography?.heading,
        uploadedFonts,
        fallbackBuiltInFont
      ),
    [fallbackBuiltInFont, typography?.heading, uploadedFonts]
  );

  const bodyFontLabel = useMemo(
    () =>
      resolveTypographyLabel(
        typography?.body,
        uploadedFonts,
        fallbackBuiltInFont
      ),
    [fallbackBuiltInFont, typography?.body, uploadedFonts]
  );

  const checks = useMemo(
    () => [
      { label: "Company name", done: !!brand?.company_name },
      {
        label: "Branding set",
        done: !!brand?.primary_color && (!!brand?.logo_url || brand?.show_logo === false),
      },
      { label: "Cover letter", done: !!brand?.cover_letter_html },
      { label: "Services added", done: services.length > 0 },
      { label: "Static pages added", done: activePages.length > 0 },
    ],
    [
      activePages.length,
      brand?.company_name,
      brand?.cover_letter_html,
      brand?.logo_url,
      brand?.primary_color,
      brand?.show_logo,
      services.length,
    ]
  );

  const allGood = checks.every((c) => c.done);
  const primary = brand?.primary_color ?? "#1a1a1a";
  const accent = brand?.accent_color ?? "#FF6B00";
  const secondary = brand?.secondary_color ?? "#1F2937";
  const background = brand?.background_color ?? "#FFFFFF";
  const textColor = brand?.text_color ?? "#111827";
  const mutedColor = brand?.muted_color ?? "#6B7280";

  const summaryRows = useMemo(
    () => [
      {
        label: "Logo style",
        value: brand?.show_logo
          ? brand?.logo_url
            ? "Logo shown in proposal branding"
            : "Logo enabled, not uploaded yet"
          : "Minimal, no main logo",
      },
      {
        label: "Colors",
        value: `${primary} / ${accent}`,
      },
      {
        label: "Heading font",
        value: headingFontLabel,
      },
      {
        label: "Body font",
        value: bodyFontLabel,
      },
      {
        label: "Uploaded fonts",
        value: `${uploadedFonts.length} uploaded custom font${uploadedFonts.length === 1 ? "" : "s"}`,
      },
      {
        label: "Pages",
        value: `${activePages.length} page${activePages.length === 1 ? "" : "s"}`,
      },
      {
        label: "Services",
        value: `${services.length} service${services.length === 1 ? "" : "s"} in catalog`,
      },
    ],
    [
      accent,
      activePages.length,
      bodyFontLabel,
      brand?.logo_url,
      brand?.show_logo,
      headingFontLabel,
      primary,
      services.length,
      uploadedFonts.length,
    ]
  );

  const handleFinish = async () => {
    await upsert.mutateAsync({
      default_builder_experience: builderExperience,
      preview_preferences: {
        ...(brand?.preview_preferences ?? {}),
        setupPreviewCompletedAt: new Date().toISOString(),
      },
    });

    onComplete();
  };

  const isBusy = isCompleting || upsert.isPending;

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <p className="mb-3 text-sm font-medium text-white/80">
          Publish your brand foundation
        </p>
        <p className="text-sm leading-6 text-white/45">
          This is your brand foundation — the company details, colors, logo, cover letter,
          services, and pages that every proposal will inherit. This preview shows
          how your brand appears in standard proposals. The drag-and-drop canvas builder
          gives you full layout control on top of this same foundation.
        </p>
      </div>

      <div className="space-y-5">
        {/* Setup checklist */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="mb-4 text-sm font-medium text-white/70">Setup checklist</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {checks.map((c) => (
              <div
                key={c.label}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                  c.done
                    ? "border-emerald-500/20 bg-emerald-500/5"
                    : "border-white/5 bg-white/[0.02]"
                }`}
              >
                <CheckCircle2
                  className={`h-4 w-4 shrink-0 ${c.done ? "text-emerald-400" : "text-white/15"}`}
                />
                <span className={`text-sm ${c.done ? "text-white/80" : "text-white/30"}`}>
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Brand preview */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-[0.15em] text-white/30">
              Brand preview
            </p>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs text-white/30 underline underline-offset-2 transition-colors hover:text-white/60"
            >
              Expand full preview
            </button>
          </div>

          {/* Compact thumbnail — same component as the full modal, scaled down */}
          <div
            className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/10 shadow-2xl transition-colors hover:border-white/25"
            style={{ height: "280px" }}
            onClick={() => setExpanded(true)}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "200%",
                transformOrigin: "top left",
                transform: "scale(0.5)",
                pointerEvents: "none",
              }}
            >
              <BrandProposalPreviewCard
                primary={primary}
                secondary={secondary}
                accent={accent}
                background={background}
                textColor={textColor}
                mutedColor={mutedColor}
                companyName={brand?.company_name}
                tagline={brand?.tagline}
                primaryLogoUrl={primaryLogoUrl}
                secondaryLogoUrl={secondaryLogoUrl}
                coverLetterHtml={brand?.cover_letter_html}
                services={services}
                activePages={activePages}
                extraBranding={extraBranding}
                uploadedFonts={uploadedFonts}
                headingFontLabel={headingFontLabel}
                bodyFontLabel={bodyFontLabel}
              />
            </div>
            {/* Hover overlay hint */}
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/30 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
              <span className="text-[11px] text-white/60">Click to expand full preview</span>
            </div>
          </div>
        </div>

        {/* Setup summary */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="mb-4 text-sm font-medium text-white/80">Setup summary</p>
          <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {summaryRows.map((row) => (
              <div
                key={row.label}
                className="flex items-start justify-between gap-4 border-b border-white/5 pb-3 last:border-b-0 last:pb-0 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:[&:nth-last-child(-n+2)]:pb-0"
              >
                <span className="text-sm text-white/40">{row.label}</span>
                <span className="text-right text-sm text-white/80">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Brand → Templates → Proposals */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="mb-4 text-sm font-medium text-white/80">
            Brand Setup → Templates → Proposals
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <Building2 className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Brand Setup</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                Defines the shared company foundation.
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <LayoutTemplate className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Templates</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                Choose services and pages per job type.
              </p>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/20 p-4">
              <FileText className="h-4 w-4 text-white/35" />
              <p className="mt-3 text-sm font-medium text-white/80">Proposals</p>
              <p className="mt-1 text-xs leading-6 text-white/40">
                Final proposals inherit that foundation and get customized.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/10 pt-2">
        {!allGood ? (
          <p className="text-xs text-white/40">
            Some items are incomplete. You can still publish this setup and keep refining it later.
          </p>
        ) : (
          <p className="text-xs text-white/40">
            Your brand foundation is ready to power templates, proposals, and future builder experiences.
          </p>
        )}

        <button
          type="button"
          onClick={() => void handleFinish()}
          disabled={isBusy}
          className="ml-auto flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowRight className="h-4 w-4" />
          )}
          {allGood ? "Publish brand foundation" : "Publish anyway"}
        </button>
      </div>

      {expanded && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          />

          <div className="fixed inset-4 z-50 flex flex-col overflow-hidden rounded-2xl border border-white/15 bg-[#111] shadow-2xl md:inset-8">
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-6 py-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-white/40" />
                <p className="text-sm font-medium text-white/70">
                  Full brand foundation preview
                </p>
                <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] text-white/25">
                  Sample data
                </span>
              </div>

              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="rounded-lg p-1.5 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-[#0f0f0f] px-6 py-6">
              <div className="mx-auto max-w-5xl space-y-6">
                <BrandProposalPreviewCard
                  primary={primary}
                  secondary={secondary}
                  accent={accent}
                  background={background}
                  textColor={textColor}
                  mutedColor={mutedColor}
                  companyName={brand?.company_name}
                  tagline={brand?.tagline}
                  primaryLogoUrl={primaryLogoUrl}
                  secondaryLogoUrl={secondaryLogoUrl}
                  coverLetterHtml={brand?.cover_letter_html}
                  services={services}
                  activePages={activePages}
                  extraBranding={extraBranding}
                  uploadedFonts={uploadedFonts}
                  headingFontLabel={headingFontLabel}
                  bodyFontLabel={bodyFontLabel}
                />

                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-sm font-medium text-white/70">Preview note</p>
                  <p className="mt-2 text-sm leading-6 text-white/40">
                    This preview shows how your brand appears in standard proposals.
                    When you use the drag-and-drop canvas builder, you get full
                    control over layout and structure on top of this same brand foundation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared proposal preview card — used by both the compact thumbnail and
// the expanded full-preview modal so they always show identical content.
// ---------------------------------------------------------------------------

interface BrandProposalPreviewCardProps {
  primary:         string;
  secondary:       string;
  accent:          string;
  background:      string;
  textColor:       string;
  mutedColor:      string;
  companyName:     string | null | undefined;
  tagline:         string | null | undefined;
  primaryLogoUrl:  string | null;
  secondaryLogoUrl:string | null;
  coverLetterHtml: string | null | undefined;
  services:        Array<{ id: string; name: string; default_frequency?: string | null; default_price?: number | null }>;
  activePages:     Array<{ id: string; title: string }>;
  extraBranding:   Array<{ id: string }>;
  uploadedFonts:   BrandFont[];
  headingFontLabel:string;
  bodyFontLabel:   string;
}

function BrandProposalPreviewCard({
  primary, secondary, accent, background, textColor, mutedColor,
  companyName, tagline, primaryLogoUrl, secondaryLogoUrl, coverLetterHtml,
  services, activePages, extraBranding, uploadedFonts, headingFontLabel, bodyFontLabel,
}: BrandProposalPreviewCardProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white">
      {/* ── Header: brand gradient ── */}
      <div
        className="relative px-8 py-10 text-white"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
      >
        <div className="flex items-center gap-4">
          {primaryLogoUrl ? (
            <img
              src={primaryLogoUrl}
              alt={companyName ?? "Logo"}
              className="max-h-12 object-contain brightness-0 invert"
            />
          ) : (
            <div className="text-3xl font-bold">{companyName ?? "Your Company"}</div>
          )}
          {secondaryLogoUrl ? (
            <img
              src={secondaryLogoUrl}
              alt="Secondary logo"
              className="max-h-8 object-contain brightness-0 invert opacity-80"
            />
          ) : null}
        </div>

        {tagline ? (
          <p className="mt-3 max-w-xl text-sm text-white/80">{tagline}</p>
        ) : null}

        <div
          className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full opacity-25"
          style={{ background: accent }}
        />
      </div>

      {/* ── Body: two-column cards ── */}
      <div className="grid gap-6 p-6 lg:grid-cols-[1.1fr_0.9fr]" style={{ background: background }}>
        <div className="space-y-4">
          <InnerCard icon={<Building2 className="h-4 w-4" />} title="Prepared for">
            <p className="font-semibold" style={{ color: textColor }}>
              {SAMPLE_PROPOSAL.prospect_name}
            </p>
            <p className="text-sm" style={{ color: mutedColor }}>
              {SAMPLE_PROPOSAL.prospect_address}
            </p>
            <p className="text-sm" style={{ color: mutedColor }}>
              {SAMPLE_PROPOSAL.prospect_city}, {SAMPLE_PROPOSAL.prospect_state}{" "}
              {SAMPLE_PROPOSAL.prospect_zip}
            </p>
          </InnerCard>

          <InnerCard icon={<Palette className="h-4 w-4" />} title="Cover letter / brand voice">
            <p className="text-sm leading-6" style={{ color: textColor }}>
              {coverLetterHtml
                ? "Your reusable cover letter foundation is set and will be used in generated proposal PDFs."
                : "No cover letter saved yet. Proposal PDFs can still generate without your branded cover letter section."}
            </p>
          </InnerCard>

          <InnerCard icon={<FileStack className="h-4 w-4" />} title="Project overview">
            <p className="text-sm leading-6" style={{ color: textColor }}>
              {SAMPLE_PROPOSAL.ai_project_summary}
            </p>
          </InnerCard>
        </div>

        <div className="space-y-4">
          <InnerCard icon={<Wrench className="h-4 w-4" />} title="Included services">
            {services.length > 0 ? (
              <div className="space-y-3">
                {services.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border px-3 py-2"
                    style={{ borderColor: `${mutedColor}33`, background: `${mutedColor}0a` }}
                  >
                    <p className="text-sm font-semibold" style={{ color: textColor }}>{item.name}</p>
                    <p className="mt-1 text-xs" style={{ color: mutedColor }}>
                      {item.default_frequency ?? "No frequency"} ·{" "}
                      {item.default_price != null ? `$${item.default_price}` : "No default price"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: mutedColor }}>No services added yet.</p>
            )}
          </InnerCard>

          <InnerCard icon={<FileText className="h-4 w-4" />} title="Reusable static pages">
            {activePages.length > 0 ? (
              <div className="space-y-2">
                {activePages.map((page) => (
                  <div
                    key={page.id}
                    className="rounded-xl border px-3 py-2"
                    style={{ borderColor: `${mutedColor}33`, background: `${mutedColor}0a` }}
                  >
                    <p className="text-sm font-semibold" style={{ color: textColor }}>{page.title}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: mutedColor }}>No active static pages yet.</p>
            )}
          </InnerCard>

          <InnerCard icon={<Type className="h-4 w-4" />} title="Brand assets and fonts">
            <div className="space-y-2 text-sm" style={{ color: textColor }}>
              <p>Extra branding assets: <span className="font-semibold">{extraBranding.length}</span></p>
              <p>Uploaded fonts: <span className="font-semibold">{uploadedFonts.length}</span></p>
              <p>Heading font: <span className="font-semibold">{headingFontLabel}</span></p>
              <p>Body font: <span className="font-semibold">{bodyFontLabel}</span></p>
            </div>
          </InnerCard>
        </div>
      </div>
    </div>
  );
}

function InnerCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
        <span className="text-gray-400">{icon}</span>
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}